import { StatusEnum } from '@albatrosdeveloper/ave-models-package/lib/enums';
import BusinessPartnerAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/business-partners/business-partner/business-partner.entity';
import AddressAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/common/embedded/address/address.entity';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DrawerFooterComponent } from 'app/shared/components/drawer/drawer-footer/drawer-footer.component';
import { DrawerHeaderComponent } from 'app/shared/components/drawer/drawer-header/drawer-header.component';
import { DrawerComponent } from 'app/shared/components/drawer/drawer.component';
import { FormSectionComponent } from 'app/shared/components/form-ui/form-section/form-section.component';
import { DRAWER_DATA, DrawerRef } from 'app/shared/services/drawer';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ClientFrequencyDisplayComponent } from '../../routing/sub-routes/client-route-locator/client-frequency-display/client-frequency-display.component';
import {
  BusinessPartnerWithRoutesResponse,
  SubRouteInfo,
} from '../../routing/sub-routes/client-route-locator/interfaces/client-route-locator.interface';
import { BusinessPartnerService } from '../business-partner/business-partner.service';
import { resolveBpCompanyEntry } from '../business-partner/business-partner-list.util';
import { CustomerDetailDrawerData, FormattedAddress } from './customer-detail-drawer.types';

@Component({
  selector: 'app-customer-detail-drawer',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatChipsModule,
    MatDividerModule,
    MatTooltipModule,
    DrawerComponent,
    DrawerHeaderComponent,
    DrawerFooterComponent,
    FormSectionComponent,
    ClientFrequencyDisplayComponent,
  ],
  templateUrl: './customer-detail-drawer.component.html',
  styles: [
    `
      :host {
        width: 100%;
      }

      .loading-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 300px;
        gap: 1rem;
      }

      .error-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 300px;
        gap: 1rem;
        color: rgba(0, 0, 0, 0.54);
      }

      .drawer-body {
        padding: 0.75rem;
        display: flex;
        flex-direction: column;
        gap: 1rem;
        min-width: 0;
      }

      @media (min-width: 640px) {
        .drawer-body {
          padding: 1.25rem;
          gap: 1.25rem;
        }
      }

      @media (min-width: 768px) {
        .drawer-body {
          padding: 1.25rem 1.35rem;
          gap: 1.3rem;
        }
      }

      @media (min-width: 1024px) {
        .drawer-body {
          padding: 1.5rem;
          gap: 1.5rem;
        }
      }

      @media (min-width: 1280px) {
        .drawer-body {
          padding: 1.5rem 1.75rem;
          gap: 1.625rem;
        }
      }

      @media (min-width: 1536px) {
        .drawer-body {
          padding: 1.75rem 2rem;
          gap: 1.75rem;
        }
      }

      .info-grid {
        display: grid;
        grid-template-columns: 1fr;
        gap: 0.75rem;
        min-width: 0;
      }

      @media (min-width: 640px) {
        .info-grid {
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 1rem;
        }
      }

      @media (min-width: 1280px) {
        .info-grid {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }
      }

      .info-item {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
      }

      .info-label {
        font-size: 0.75rem;
        color: rgba(0, 0, 0, 0.54);
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .info-value {
        font-size: 0.875rem;
        color: rgba(0, 0, 0, 0.87);
      }

      .info-value.empty {
        color: rgba(0, 0, 0, 0.38);
        font-style: italic;
      }

      .info-value,
      .contact-value,
      .address-main,
      .address-secondary {
        overflow-wrap: anywhere;
        word-break: break-word;
      }

      .full-width {
        grid-column: 1 / -1;
      }

      .status-badge {
        display: inline-flex;
        align-items: center;
        padding: 0.25rem 0.75rem;
        border-radius: 9999px;
        font-size: 0.75rem;
        font-weight: 500;
      }

      .status-active {
        background: rgba(34, 197, 94, 0.12);
        color: rgb(22, 163, 74);
      }

      .status-inactive {
        background: rgba(239, 68, 68, 0.12);
        color: rgb(220, 38, 38);
      }

      .contact-list {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
      }

      .contact-item {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        min-width: 0;
        padding: 0.5rem;
        background: rgba(0, 0, 0, 0.02);
        border-radius: 0.5rem;
      }

      .contact-icon {
        color: rgba(var(--fuse-primary-rgb), 1);
        font-size: 20px;
        width: 20px;
        height: 20px;
      }

      .contact-info {
        flex: 1;
        min-width: 0;
      }

      .contact-value {
        font-size: 0.875rem;
        color: rgba(0, 0, 0, 0.87);
      }

      .contact-type {
        font-size: 0.75rem;
        color: rgba(0, 0, 0, 0.54);
      }

      .address-card {
        padding: 0.75rem;
        background: rgba(0, 0, 0, 0.02);
        border-radius: 0.5rem;
        border-left: 3px solid rgba(var(--fuse-primary-rgb), 1);
      }

      .address-main {
        font-size: 0.875rem;
        color: rgba(0, 0, 0, 0.87);
        margin-bottom: 0.25rem;
      }

      .address-secondary {
        font-size: 0.75rem;
        color: rgba(0, 0, 0, 0.54);
      }

      .route-chip {
        display: inline-flex;
        align-items: center;
        gap: 0.25rem;
        max-width: 100%;
        padding: 0.25rem 0.75rem;
        background: rgba(var(--fuse-primary-rgb), 0.08);
        color: rgba(var(--fuse-primary-rgb), 1);
        border-radius: 9999px;
        font-size: 0.75rem;
        font-weight: 500;
        box-sizing: border-box;
        white-space: normal;
        text-align: left;
      }

      .routes-container {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        min-width: 0;
      }

      .geo-status {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        min-width: 0;
        padding: 0.75rem;
        border-radius: 0.5rem;
      }

      .geo-status span {
        min-width: 0;
        overflow-wrap: anywhere;
      }

      .geo-valid {
        background: rgba(34, 197, 94, 0.08);
        color: rgb(22, 163, 74);
      }

      .geo-invalid {
        background: rgba(239, 68, 68, 0.08);
        color: rgb(220, 38, 38);
      }

      .empty-section {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 1.5rem;
        color: rgba(0, 0, 0, 0.38);
        text-align: center;
      }

      .route-frequency-card {
        padding: 0.75rem;
        background: rgba(0, 0, 0, 0.02);
        border-radius: 0.5rem;
        border-left: 3px solid rgba(var(--fuse-primary-rgb), 1);
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }

      .route-header {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        min-width: 0;
      }

      .route-name {
        font-size: 0.875rem;
        font-weight: 500;
        color: rgba(0, 0, 0, 0.87);
        overflow-wrap: anywhere;
      }

      .route-frequency-body {
        padding-left: 0.25rem;
      }

      .no-frequency-badge {
        display: inline-flex;
        padding: 0.25rem 0.5rem;
        font-size: 0.75rem;
        background: rgba(239, 68, 68, 0.08);
        color: rgb(220, 38, 38);
        border-radius: 9999px;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerDetailDrawerComponent implements OnInit {
  readonly drawerRef = inject(DrawerRef);
  readonly data: CustomerDetailDrawerData = inject(DRAWER_DATA);
  private readonly businessPartnerService = inject(BusinessPartnerService);
  private readonly destroyRef = inject(DestroyRef);

  // State signals
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly customer = signal<BusinessPartnerAttributes | null>(null);

  // Computed values
  readonly customerName = computed(() => this.customer()?.name ?? 'Cliente');
  readonly customerCode = computed(() => {
    const c = this.customer();
    return c?.externalKeys?.code || c?.code || '';
  });

  readonly isActive = computed(() => this.customer()?.statusId === StatusEnum.ACTIVE);

  /**
   * The customer's `bpCompanies[]` entry for the company this drawer was opened
   * from. `undefined` when no company was passed (legacy caller) or the customer
   * has no entry for it (not migrated) — callers then fall back to the ROOT copy.
   *
   * `resolveBpCompanyEntry` returns `bpCompanies[0]` when handed an empty scope,
   * so it is only consulted once a company id is actually present; otherwise the
   * drawer would show an arbitrary company's values.
   */
  private readonly companyEntry = computed(() => {
    const companyId = this.data.companyId;
    if (!companyId) return undefined;
    return resolveBpCompanyEntry(this.customer(), {
      seesAll: false,
      allowedCompanyIds: [String(companyId)],
    });
  });

  /**
   * Group / sub-group / route area FOR THE COMPANY in context. The matching
   * fields on the business partner root are deprecated; they are used only as a
   * fallback for customers with no entry for this company.
   */
  readonly businessPartnerGroup = computed(
    () => this.companyEntry()?.businessPartnerGroup ?? this.customer()?.businessPartnerGroup
  );
  readonly businessPartnerSubGroup = computed(
    () => this.companyEntry()?.businessPartnerSubGroup ?? this.customer()?.businessPartnerSubGroup
  );
  readonly routeArea = computed(
    () => this.companyEntry()?.routeArea ?? this.customer()?.routeArea
  );

  /**
   * Credit FOR THE COMPANY in context. The partner root still carries the same
   * fields but they are the deprecated pre-multicompany copies, so they are used
   * only when this partner has no entry for the company (not migrated).
   */
  readonly creditLimit = computed(
    () => this.companyEntry()?.creditLimit ?? this.customer()?.creditLimit ?? null
  );
  readonly creditAvailable = computed(
    () => this.companyEntry()?.creditAvailable ?? this.customer()?.creditAvailable ?? null
  );
  readonly isCreditBlocked = computed(
    () => this.companyEntry()?.isCreditBlocked ?? this.customer()?.isCreditBlocked ?? false
  );
  readonly creditBlockReason = computed(
    () => this.companyEntry()?.creditBlockReason ?? this.customer()?.creditBlockReason ?? ''
  );

  /** Whether there is anything worth rendering in the credit section. */
  readonly hasCreditInfo = computed(
    () => this.creditLimit() != null || this.creditAvailable() != null || this.isCreditBlocked()
  );

  readonly phones = computed(() => this.customer()?.phones ?? []);
  readonly emails = computed(() => this.customer()?.emails ?? []);
  readonly subRoutesWithFrequencies = signal<SubRouteInfo[]>([]);
  readonly subRoutes = this.subRoutesWithFrequencies.asReadonly();

  /** Pre-formatted addresses for display (avoids functions in template) */
  readonly formattedAddresses = computed<FormattedAddress[]>(() => {
    const addresses = this.customer()?.addresses ?? [];
    return addresses.map((address) => ({
      original: address,
      formattedAddress: this.buildFormattedAddress(address),
      locationCodes: this.buildLocationCodes(address),
      reference: address.reference || null,
    }));
  });

  readonly hasValidGeolocation = computed(() => {
    const c = this.customer();
    const principalAddress = c?.addresses?.find((a) => a.isPrincipal);
    return principalAddress?.geolocationPoint?.length === 2;
  });

  ngOnInit(): void {
    this.loadCustomer();
  }

  private loadCustomer(): void {
    this.loading.set(true);
    this.error.set(null);

    forkJoin({
      customer: this.businessPartnerService.findById(this.data.customerId),
      withRoutes: this.businessPartnerService
        .findBusinessPartnerWithRoutesById(this.data.customerId)
        .pipe(
          catchError(() =>
            of({ data: { data: [], pageInfo: [] } } as unknown as BusinessPartnerWithRoutesResponse)
          )
        ),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ customer, withRoutes }) => {
          if (customer.data) {
            this.customer.set(customer.data);
            this.subRoutesWithFrequencies.set(withRoutes?.data?.data?.[0]?.subRoutes ?? []);
          } else {
            this.error.set('Cliente no encontrado');
          }
          this.loading.set(false);
        },
        error: () => {
          this.error.set('Error al cargar los datos del cliente');
          this.loading.set(false);
        },
      });
  }

  close(): void {
    this.drawerRef.close();
  }

  retry(): void {
    this.loadCustomer();
  }

  onFrequencyRefresh(): void {
    this.loadCustomer();
  }

  /**
   * Build formatted address string from address attributes.
   */
  private buildFormattedAddress(address: AddressAttributes): string {
    // The address is stored as a single string in AddressAttributes
    // We can complement it with location names if available
    if (address.address) {
      return address.address;
    }

    // Fallback: try to build from location names
    const locationParts = [
      address.locationFour?.name,
      address.locationThree?.name,
      address.locationTwo?.name,
      address.locationOne?.name,
    ].filter(Boolean);

    return locationParts.join(', ') || 'Sin dirección';
  }

  /**
   * Build location codes string from address attributes.
   */
  private buildLocationCodes(address: AddressAttributes): string {
    const parts = [
      address.locationOne?.code,
      address.locationTwo?.code,
      address.locationThree?.code,
      address.locationFour?.code,
    ].filter(Boolean);
    return parts.join(' / ');
  }
}
